import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Button, Card, Col, Modal, Progress, Row, Select, Space, Spin, Table, Tag, Tooltip,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined, ExportOutlined, LinkOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  ACTIVE_RUN_STATUSES,
  AU_SALES_PORTFOLIO_URL,
  CHANGE_STATUS_OPTIONS,
  CHANGE_SYSTEM_OPTIONS,
  CIN7_URL,
  CRM_BOARD_URL,
  NZ_SALES_PORTFOLIO_URL,
  REGION_LABELS,
  RUN_STATUS_CONFIG,
} from '../constants';
import {
  formatTimestamp, getSystemPercent, pickTimestamp,
} from '../runUtils';
import DistributionSummary from '../DistributionSummary';
import DataProcessedCard from '../DataProcessedCard';
import RunChecks from '../RunChecks';
import RepUpdateCopy from '../RepUpdateCopy';
import useSegmentationRunQuery from './use-segmentation-run-query';
import useSegmentationChangesQuery from './use-segmentation-changes-query';
import useApproveSegmentationRunMutation from './use-approve-segmentation-run-mutation';
import useRejectSegmentationRunMutation from './use-reject-segmentation-run-mutation';
import useRetrySegmentationRunMutation from './use-retry-segmentation-run-mutation';

const { Text, Title, Paragraph } = Typography;

const STARTED_FIELDS = ['createdAt', 'startedAt', 'scoringStartedAt'];
const UPDATED_FIELDS = ['completedAt', 'appliedAt', 'updatedAt'];

const CHANGE_STATUS_COLOR = {
  ok: 'green', failed: 'red', skipped: 'default', pending: 'processing',
};

const statusTag = (status) => {
  const config = RUN_STATUS_CONFIG[status] ?? { label: status ?? 'Unknown', color: 'default' };
  return <Tag color={config.color}>{config.label}</Tag>;
};

const openExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');

const SystemProgress = ({
  title, total, ok, fail, skip,
}) => {
  const done = ok + fail + (skip ?? 0);
  const percent = getSystemPercent(done, total);
  let progressStatus = 'normal';
  if (fail > 0) progressStatus = 'exception';
  else if (total && done >= total) progressStatus = 'success';
  return (
    <div className="segmentation-system-progress">
      <div className="segmentation-system-progress-head">
        <Text strong>{title}</Text>
        <Text type="secondary">
          {done}
          {' / '}
          {total}
        </Text>
      </div>
      <Progress percent={percent} status={progressStatus} size="small" />
      <Space size="small" wrap style={{ marginTop: 4 }}>
        <Tag color="green">{`OK ${ok}`}</Tag>
        <Tag color="red">{`Fail ${fail}`}</Tag>
        {skip != null && <Tag>{`Skip ${skip}`}</Tag>}
      </Space>
    </div>
  );
};

SystemProgress.propTypes = {
  title: PropTypes.string.isRequired,
  total: PropTypes.number,
  ok: PropTypes.number,
  fail: PropTypes.number,
  skip: PropTypes.number,
};

SystemProgress.defaultProps = {
  total: 0,
  ok: 0,
  fail: 0,
  skip: null,
};

const RunDetail = ({ runId, onBack }) => {
  const queryClient = useQueryClient();

  const [systemFilter, setSystemFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data: run, isLoading: runLoading } = useSegmentationRunQuery(runId, {
    // Poll while the run is active so progress shows without a reload.
    refetchInterval: (data) => (
      ACTIVE_RUN_STATUSES.includes(data?.status) ? 5000 : false
    ),
  });

  const status = run?.status;
  const isApplyingOrDone = status === 'applying' || status === 'completed' || status === 'failed';

  const {
    data: changesData,
    isFetching: changesFetching,
  } = useSegmentationChangesQuery(runId, {
    system: systemFilter,
    status: statusFilter,
    limit: pageSize,
    offset: (page - 1) * pageSize,
    enabled: isApplyingOrDone,
  });

  const approveMutation = useApproveSegmentationRunMutation({
    onSuccess: () => {
      queryClient.invalidateQueries(['segmentation', 'run', runId]);
      queryClient.invalidateQueries(['segmentation', 'runs']);
    },
  });

  const rejectMutation = useRejectSegmentationRunMutation({
    onSuccess: () => {
      queryClient.invalidateQueries(['segmentation', 'run', runId]);
      queryClient.invalidateQueries(['segmentation', 'runs']);
    },
  });

  const retryMutation = useRetrySegmentationRunMutation({
    onSuccess: () => {
      queryClient.invalidateQueries(['segmentation', 'run', runId]);
      queryClient.invalidateQueries(['segmentation', 'runs']);
      queryClient.invalidateQueries(['segmentation', 'changes', runId]);
    },
  });

  const changeRows = useMemo(
    () => (Array.isArray(changesData?.rows) ? changesData.rows : []),
    [changesData],
  );
  const changeTotal = changesData?.total ?? 0;

  const onApprove = () => {
    const failChecks = (Array.isArray(run?.summaryJson?.checks) ? run.summaryJson.checks : [])
      .filter((c) => c.level === 'fail');
    Modal.confirm({
      title: 'Approve and apply this run?',
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      content: (
        <div>
          <p>
            This writes the new segments to Cin7 and Monday for
            {' '}
            <strong>{REGION_LABELS[run?.region] ?? run?.region}</strong>
            {' '}
            customers. Diff-based — only changed customers are updated.
          </p>
          {failChecks.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 12 }}
              message="Fail-level checks on this run"
              description={(
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  {failChecks.map((c) => (
                    <li key={c.id || c.message}>{c.message}</li>
                  ))}
                </ul>
              )}
            />
          )}
        </div>
      ),
      okText: failChecks.length > 0 ? 'Approve anyway' : 'Approve & apply',
      okButtonProps: failChecks.length > 0 ? { danger: true } : undefined,
      onOk: () => approveMutation.mutateAsync(runId).catch(() => {}),
    });
  };

  const failCount = (run?.cin7Fail || 0) + (run?.mondayFail || 0);
  const canRetry = (status === 'failed' && !!run?.scoringFinishedAt)
    || (status === 'completed' && failCount > 0);

  const onRetry = () => {
    Modal.confirm({
      title: 'Retry this apply?',
      icon: <ReloadOutlined />,
      content: 'Writes this run to Cin7 and Monday again. Customers already updated are skipped. '
        + 'Failures stay on this run.',
      okText: 'Retry apply',
      onOk: () => retryMutation.mutateAsync(runId).catch(() => {}),
    });
  };

  const onReject = () => {
    Modal.confirm({
      title: 'Reject this run?',
      icon: <CloseCircleOutlined style={{ color: '#cf1322' }} />,
      content: 'The scored segments will be discarded and nothing will be written to Cin7 or Monday.',
      okText: 'Reject run',
      okButtonProps: { danger: true },
      onOk: () => rejectMutation.mutateAsync(runId).catch(() => {}),
    });
  };

  const changeFilters = (
    <Space wrap>
      <Select
        allowClear
        placeholder="System"
        style={{ width: 140 }}
        value={systemFilter}
        onChange={(value) => { setSystemFilter(value ?? null); setPage(1); }}
        options={CHANGE_SYSTEM_OPTIONS}
      />
      <Select
        allowClear
        placeholder="Status"
        style={{ width: 140 }}
        value={statusFilter}
        onChange={(value) => { setStatusFilter(value ?? null); setPage(1); }}
        options={CHANGE_STATUS_OPTIONS}
      />
    </Space>
  );

  const portfolioLink = run?.region === 'AU'
    ? { label: 'AU Sales Portfolio', url: AU_SALES_PORTFOLIO_URL }
    : { label: 'NZ Sales Portfolio', url: NZ_SALES_PORTFOLIO_URL };

  const crmBoardLink = run?.region === 'AU'
    ? { label: 'AU Monday CRM board', url: CRM_BOARD_URL.AU }
    : { label: 'NZ Monday CRM board', url: CRM_BOARD_URL.NZ };

  if (runLoading && !run) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (!run) {
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} style={{ marginBottom: 16 }}>
          Back to runs
        </Button>
        <Alert type="error" showIcon message="Run not found." />
      </div>
    );
  }

  return (
    <div className="segmentation-run-detail">
      <div className="segmentation-detail-header">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} />
          <Title level={4} style={{ margin: 0 }}>
            {`${REGION_LABELS[run.region] ?? run.region} run #${run.id}`}
          </Title>
          {statusTag(status)}
          {run.autoApply && <Tag color="volcano">Auto-apply</Tag>}
          {(run.summaryJson?.testMode || run.summaryJson?.scoring?.testMode) && (
            <Tag color="red">Test mode</Tag>
          )}
        </Space>
        <Space size="large" wrap>
          <Text type="secondary">
            {'Started: '}
            {formatTimestamp(pickTimestamp(run, STARTED_FIELDS))}
          </Text>
          <Text type="secondary">
            {'Updated: '}
            {formatTimestamp(pickTimestamp(run, UPDATED_FIELDS))}
          </Text>
        </Space>
      </div>

      {run.errorMessage && (status === 'failed' || status === 'completed') && (
        <Alert
          type={status === 'failed' ? 'error' : 'warning'}
          showIcon
          className="segmentation-section"
          message={status === 'failed' ? 'Run failed' : 'Last retry did not finish'}
          description={run.errorMessage}
        />
      )}

      <DataProcessedCard run={run} />

      <Card className="segmentation-section" title="Segment distribution">
        <Paragraph type="secondary">
          Compare this score against live Cin7 segments (Monday if Cin7 is
          unreachable). A big swing usually means scoring is off.
        </Paragraph>
        <RunChecks checks={run.summaryJson?.checks} />
        <DistributionSummary summary={run.summaryJson} />

        {status === 'awaiting_approval' && (
          <div className="segmentation-approval-actions">
            <Space wrap>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={approveMutation.isLoading}
                onClick={onApprove}
              >
                Approve &amp; apply
              </Button>
              <Button
                danger
                icon={<CloseCircleOutlined />}
                loading={rejectMutation.isLoading}
                onClick={onReject}
              >
                Reject
              </Button>
            </Space>
          </div>
        )}
      </Card>

      {(status === 'awaiting_approval' || status === 'completed') && (
        <RepUpdateCopy run={run} />
      )}

      {isApplyingOrDone && (
        <Card
          className="segmentation-section"
          title="Apply progress"
          extra={canRetry ? (
            <Button
              icon={<ReloadOutlined />}
              loading={retryMutation.isLoading}
              onClick={onRetry}
            >
              Retry apply
            </Button>
          ) : null}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} md={12}>
              <SystemProgress
                title="Cin7"
                total={run.cin7Total}
                ok={run.cin7Ok}
                fail={run.cin7Fail}
                skip={run.cin7Skip}
              />
            </Col>
            <Col xs={24} md={12}>
              <SystemProgress
                title="Monday"
                total={run.mondayTotal}
                ok={run.mondayOk}
                fail={run.mondayFail}
              />
            </Col>
          </Row>
        </Card>
      )}

      {isApplyingOrDone && (
        <Card
          className="segmentation-section"
          title="Change log"
          extra={changeFilters}
        >
          <Table
            size="small"
            rowKey={(row, index) => row.id ?? `${row.customerId}-${row.field}-${index}`}
            loading={changesFetching}
            dataSource={changeRows}
            locale={{ emptyText: 'No changes match the current filters.' }}
            pagination={{
              current: page,
              pageSize,
              total: changeTotal,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100'],
              showTotal: (total) => `${total} changes`,
            }}
            onChange={(pagination) => {
              setPage(pagination.current);
              setPageSize(pagination.pageSize);
            }}
            columns={[
              {
                title: 'Customer',
                key: 'customer',
                render: (_, row) => row.customerName || row.customerId || '—',
              },
              {
                title: 'System',
                dataIndex: 'system',
                key: 'system',
                width: 100,
                render: (system) => <Tag>{system}</Tag>,
              },
              {
                title: 'Field',
                dataIndex: 'field',
                key: 'field',
                width: 140,
              },
              {
                title: 'Change',
                key: 'change',
                render: (_, row) => (
                  <Space size="small">
                    <Text delete type="secondary">{row.oldValue ?? '—'}</Text>
                    <span>→</span>
                    <Text strong>{row.newValue ?? '—'}</Text>
                  </Space>
                ),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (value) => (
                  <Tag color={CHANGE_STATUS_COLOR[value] ?? 'default'}>{value}</Tag>
                ),
              },
              {
                title: 'Error',
                dataIndex: 'error',
                key: 'error',
                ellipsis: true,
                render: (error) => (error ? (
                  <Tooltip title={error}>
                    <Text type="danger">{error}</Text>
                  </Tooltip>
                ) : (
                  <Text type="secondary">—</Text>
                )),
              },
            ]}
          />
        </Card>
      )}

      <Card className="segmentation-section" title="External links">
        <Space wrap>
          <Button icon={<ExportOutlined />} onClick={() => openExternal(portfolioLink.url)}>
            {portfolioLink.label}
          </Button>
          <Button icon={<ExportOutlined />} onClick={() => openExternal(CIN7_URL)}>
            Cin7
          </Button>
          <Button icon={<LinkOutlined />} onClick={() => openExternal(crmBoardLink.url)}>
            {crmBoardLink.label}
          </Button>
        </Space>
      </Card>
    </div>
  );
};

RunDetail.propTypes = {
  runId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onBack: PropTypes.func.isRequired,
};

export default RunDetail;
